/**
 * Klien untuk ejen cetak tempatan (`127.0.0.1:7321`).
 *
 * Kegagalan ejen **tidak boleh** menghalang jualan. Setiap fungsi memulangkan hasil yang boleh
 * diabaikan; tiada satu pun membaling ralat rangkaian kepada pemanggil.
 */

const AGENT_BASE = 'http://127.0.0.1:7321';
const TIMEOUT_MS = 2_000;

export interface AgentHealth {
  available: boolean;
  printerConnected: boolean;
  paperWidthMm: 58 | 80 | null;
  queued: number;
  /** Origin yang dibenarkan oleh ejen — untuk mengesan salah konfigurasi CORS. */
  allowedOrigin: string | null;
  /** Nama printer yang dikonfigurasi, jika ada. */
  printerName: string | null;
}

export interface PrintOutcome {
  printed: boolean;
  queued: boolean;
  duplicate: boolean;
  error: string | null;
  agentUnavailable: boolean;
}

const UNAVAILABLE: PrintOutcome = {
  printed: false,
  queued: false,
  duplicate: false,
  error: 'Ejen cetak tidak berjalan',
  agentUnavailable: true,
};

async function withTimeout(
  fetchImpl: typeof fetch,
  url: string,
  init: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetchImpl(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export async function agentHealth(
  token: string | undefined,
  fetchImpl: typeof fetch = fetch,
): Promise<AgentHealth> {
  try {
    const response = await withTimeout(fetchImpl, `${AGENT_BASE}/health`, { method: 'GET' });
    if (!response.ok) {
      return { available: false, printerConnected: false, paperWidthMm: null, queued: 0, allowedOrigin: null, printerName: null };
    }
    const body = (await response.json()) as {
      paper_width_mm?: number;
      allowed_origin?: string;
      printer?: { connected?: boolean; name?: string };
      queue?: { pending?: number };
    };
    void token;
    return {
      available: true,
      printerConnected: body.printer?.connected === true,
      paperWidthMm: body.paper_width_mm === 58 ? 58 : body.paper_width_mm === 80 ? 80 : null,
      queued: body.queue?.pending ?? 0,
      allowedOrigin: body.allowed_origin ?? null,
      printerName: body.printer?.name ?? null,
    };
  } catch {
    return { available: false, printerConnected: false, paperWidthMm: null, queued: 0, allowedOrigin: null, printerName: null };
  }
}

async function postJob(
  path: string,
  token: string,
  body: unknown,
  fetchImpl: typeof fetch,
): Promise<PrintOutcome> {
  try {
    const response = await withTimeout(fetchImpl, `${AGENT_BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-agent-token': token },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let message = `Ejen menolak permintaan (HTTP ${response.status})`;
      try {
        const error = (await response.json()) as { error?: { message?: string } };
        message = error.error?.message ?? message;
      } catch {
        // Bukan JSON.
      }
      return { printed: false, queued: false, duplicate: false, error: message, agentUnavailable: false };
    }

    const result = (await response.json()) as {
      printed?: boolean;
      queued?: boolean;
      duplicate?: boolean;
      error?: string;
    };
    return {
      printed: result.printed === true,
      queued: result.queued === true,
      duplicate: result.duplicate === true,
      error: result.error ?? null,
      agentUnavailable: false,
    };
  } catch {
    return UNAVAILABLE;
  }
}

export function printReceipt(
  token: string,
  job: Record<string, unknown>,
  fetchImpl: typeof fetch = fetch,
): Promise<PrintOutcome> {
  return postJob('/print/receipt', token, job, fetchImpl);
}

export function printReport(
  token: string,
  job: Record<string, unknown>,
  fetchImpl: typeof fetch = fetch,
): Promise<PrintOutcome> {
  return postJob('/print/report', token, job, fetchImpl);
}


/**
 * Rekod "buka laci tanpa jualan" pada pelayan.
 *
 * Laci digerakkan oleh ejen setempat, jadi rekod ini **tidak** menyekat pembukaan. Ia hanya
 * memastikan setiap pembukaan boleh dijejak kepada kasir dan syif. Kegagalan rangkaian diabaikan.
 */
export async function recordDrawerOpen(
  apiBase: string,
  identity: { deviceId: string },
  privateKey: string | CryptoKey,
  shiftId: string | null,
  sign: (privateKey: string | CryptoKey, timestamp: number, body: string) => Promise<string>,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const raw = JSON.stringify({ shift_id: shiftId, reason: 'no_sale' });
  const timestamp = Math.floor(Date.now() / 1000);
  try {
    const signature = await sign(privateKey, timestamp, raw);
    const response = await withTimeout(fetchImpl, `${apiBase}/v1/sync/drawer-opens`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-device-id': identity.deviceId,
        'x-device-timestamp': String(timestamp),
        'x-device-signature': signature,
      },
      body: raw,
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function openDrawer(
  token: string,
  jobId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await withTimeout(fetchImpl, `${AGENT_BASE}/drawer/open`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-agent-token': token },
      body: JSON.stringify({ job_id: jobId }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Bina badan `ReceiptJob` daripada jualan yang disimpan. */
export function receiptJobFromSale(input: {
  sale: {
    id: string;
    receipt_no: string;
    client_created_at: string;
    total_sen: number;
    paid_sen: number;
    change_sen: number;
    subtotal_sen: number;
    discount_sen: number;
    tax_sen: number;
  };
  lines: ReadonlyArray<{
    description_snapshot: string;
    qty: string;
    unit: string;
    unit_price_sen: number;
    discount_sen: number;
    tax_sen: number;
    line_total_sen: number;
  }>;
  payments: ReadonlyArray<{ method: string; amount_sen: number }>;
  cashierName?: string;
  /** Kepala kedai daripada tetapan pelayan; ejen mengatasinya berbanding agent.json. */
  receiptHeader?: readonly string[];
  /** Footer daripada tetapan pelayan. */
  receiptFooter?: string;
  openDrawer: boolean;
  footerQr?: string;
  /**
   * Dicetak semula dengan sengaja: job_id mesti BERBEZA, kerana ejen menolak job_id yang sudah
   * dicetak (idempotensi untuk cubaan semula rangkaian, bukan untuk permintaan kasir).
   */
  reprint?: boolean;
}): Record<string, unknown> {
  const { sale } = input;
  return {
    // Cetak semula mesti menggunakan id baharu. Tanpa ini, ejen menganggapnya pendua, memulangkan
    // `printed: true` tanpa mencetak apa-apa, dan kasir melihat mesej kejayaan yang palsu.
    job_id:
      input.reprint === true ? `sale-${sale.id}-r${Date.now()}` : `sale-${sale.id}`,
    copies: 1,
    open_drawer: input.openDrawer,
    template: 'standard',
    ...(input.receiptHeader === undefined ? {} : { receipt_header: input.receiptHeader }),
    ...(input.receiptFooter === undefined ? {} : { receipt_footer: input.receiptFooter }),
    sale: {
      receipt_no: sale.receipt_no,
      created_at: sale.client_created_at,
      ...(input.cashierName === undefined ? {} : { cashier_name: input.cashierName }),
      lines: input.lines.map((line) => ({
        name: line.description_snapshot,
        qty: line.qty,
        unit: line.unit,
        unit_price_sen: line.unit_price_sen,
        discount_sen: line.discount_sen,
        tax_sen: line.tax_sen,
        line_total_sen: line.line_total_sen,
      })),
      totals: {
        subtotal_sen: sale.subtotal_sen,
        discount_sen: sale.discount_sen,
        tax_sen: sale.tax_sen,
        total_sen: sale.total_sen,
        paid_sen: sale.paid_sen,
        change_sen: sale.change_sen,
      },
      payments: input.payments.map((payment) => ({
        method: payment.method,
        amount_sen: payment.amount_sen,
      })),
      ...(input.footerQr === undefined ? {} : { footer: { qr_data: input.footerQr } }),
    },
  };
}

/** Halaman ujian: pengesahan pemasangan yang paling cepat. */
export function printTest(
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PrintOutcome> {
  return postJob('/print/test', token, {}, fetchImpl);
}

/** Konfigurasi ejen (lebar kertas, kepala resit) — untuk paparan sahaja. */
export async function agentConfig(
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ paperWidthMm: number; allowedOrigin: string } | null> {
  try {
    const response = await withTimeout(fetchImpl, `${AGENT_BASE}/config`, {
      method: 'GET',
      headers: { 'x-agent-token': token },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { paper_width_mm?: number; allowed_origin?: string };
    return {
      paperWidthMm: body.paper_width_mm ?? 80,
      allowedOrigin: body.allowed_origin ?? '',
    };
  } catch {
    return null;
  }
}